import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useMemo } from 'react';
import { AppState, Platform, Alert } from 'react-native';
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js';
import { router } from 'expo-router';
import { supabase, validateSupabaseConnection } from '@/lib/supabase';
import { config } from '@/config/environment';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ConsentService } from '@/services/consentService';
import { MagicLinkService } from '@/services/magicLinkService';
import { clinicCacheService, CachedClinicData, CachedUserPlan } from '../services/clinicCacheService';
import { cacheService } from '../services/cacheService';
import oneSignalService from '@/services/oneSignalService';
import { setAccessToken, clearAccessToken, setAuthExpiredCallback } from '@/services/apiService';
import { useLanguage } from '@/contexts/LanguageContext';
import { biometricService, BIOMETRIC_SKIP_ONCE_KEY } from '@/services/biometricService';

// AsyncStorage key: written by signUp() after bootstrap, consumed (read + cleared) once
// by StartupPromptContext to suppress the username prompt for fresh signup sessions.
export const USERNAME_PROMPT_SKIP_KEY = 'username_prompt_skip_once';
// One-shot flag set by set-password.tsx after the consent record write
// succeeds — tells StartupPromptContext to skip the consent bottom sheet
// on the very next app open for a fresh signup.
export const CONSENT_PROMPT_SKIP_KEY = 'consent_prompt_skip_once';

// Input validation utilities
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
  return phoneRegex.test(phone.trim());
};

const sanitizeInput = (input: string): string => {
  return input.trim().replace(/[<>]/g, '');
};

// REMOVED: registerWithOneSignalAPI function

const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return true;
    const payload = JSON.parse(atob(parts[1]));
    const expiry = payload.exp * 1000;
    const now = Date.now();
    return now >= expiry;
  } catch (e) {
    return true;
  }
};

// T004: Extract iat (issued-at) from a JWT for race condition ordering
const getTokenIat = (token: string): number => {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return 0;
    const payload = JSON.parse(atob(parts[1]));
    return payload.iat || 0;
  } catch (e) {
    return 0;
  }
};
// OneSignal user registration is now handled by SDK's OneSignal.login(userId) 
// via oneSignalService.setExternalUserId() - this is the recommended best practice
// Server API is only used for SENDING notifications, not user creation

interface UserProfile {
  id: string;
  email: string;
  name?: string | null;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  clinic_id?: string | null;
  is_admin?: boolean;
  invitation_source?: string | null;
  interests?: string[] | null;
  username?: string | null;
  addiction_types?: string[] | null;
  addiction_frequency?: string | null;
  app_goal?: string | null;
  gender?: string | null;
  sponsorship_preference?: string | null;
}

interface EnhancedUserData {
  profile: UserProfile;
  clinic: CachedClinicData | null;
  plan: CachedUserPlan | null;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  clinicData: CachedClinicData | null;
  planData: CachedUserPlan | null;
  session: Session | null;
  sessionReady: boolean; // True when session is validated and safe for API calls
  loading: boolean;
  isInitializing: boolean;
  error: string | null;
  retryCount: number;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signOut: (opts?: { keepBiometric?: boolean }) => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: any }>;
  refreshProfile: () => Promise<UserProfile | null>;
  waitForProfileReady: (timeoutMs?: number) => Promise<UserProfile | null>;
  clearAllUserData: () => Promise<void>;
  requestAccountDeletion: () => Promise<{ error: any }>;
  retryInitialization: () => Promise<void>;
  clearError: () => void;
  recordUserConsent: (consents: any[]) => Promise<{ error: any }>;
  getUserConsents: () => Promise<{ data: any[] | null; error: any }>;
  hasConsent: (consentType: string) => Promise<{ granted: boolean; error: any }>;
  handleMagicLink: (url: string) => Promise<{ success: boolean; error?: any }>;
  isPatientUser: () => Promise<boolean>;
  needsPasswordSetup: () => Promise<boolean>;
  getClinicData: () => Promise<CachedClinicData | null>;
  getPlanData: () => Promise<CachedUserPlan | null>;
  refreshUserData: () => Promise<void>;
  invalidateUserCache: () => Promise<void>;
  /** @deprecated Consent prompts are now driven by StartupPromptContext/ConsentSheet. Retained for ConsentUpdateModal compat only. */
  needsConsentUpdate: boolean;
  /** @deprecated Use ConsentSheet via StartupPromptContext instead. */
  dismissConsentUpdate: () => void;
  acceptConsentUpdate: (accessToken: string) => Promise<{ error: any }>;
  enhancedLoading: boolean;
  enhancedReady: boolean;
  /** @deprecated Biometric prompts are now driven by StartupPromptContext/StartupPromptQueue. Retained for BiometricEnableModal standalone compat only. */
  showBiometricModal: boolean;
  /** @deprecated See showBiometricModal. */
  biometricIsFaceId: boolean;
  /** @deprecated See showBiometricModal. */
  dismissBiometricModal: () => void;
  confirmBiometricEnrolment: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useLanguage();
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [clinicData, setClinicData] = useState<CachedClinicData | null>(null);
  const [planData, setPlanData] = useState<CachedUserPlan | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [sessionReady, setSessionReady] = useState(false); // True when session is validated and safe for API calls
  const [loading, setLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [enhancedLoading, setEnhancedLoading] = useState(false);
  const [enhancedReady, setEnhancedReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [needsConsentUpdate, setNeedsConsentUpdate] = useState(false);
  const [showBiometricModal, setShowBiometricModal] = useState(false);
  const [biometricIsFaceId, setBiometricIsFaceId] = useState(false);
  const pendingBiometricSessionRef = useRef<Session | null>(null);
  
  // Ref to track current userProfile for polling (avoids closure staleness)
  const userProfileRef = useRef<UserProfile | null>(null);

  // T004: Track the iat (issued-at) of the last session we set, so we can
  // avoid overwriting a newer session with an older one during race conditions
  // between initializeAuth() and the onAuthStateChange TOKEN_REFRESHED handler.
  const latestSessionIat = useRef<number>(0);

  // Sync ref with userProfile state changes (allows polling live value)
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  // Sync apiService token with session changes for automatic 401 retry
  useEffect(() => {
    setAccessToken(session?.access_token || null);
  }, [session]);

  // Load enhanced user data (profile + clinic + plan) with caching
  // PRD_GOOGLE_AUTH §3 — detect a NEW Google user right after SIGNED_IN and
  // seed the onboarding flow so AuthGuard routes them through onboarding (their
  // profile has no name yet). Email users and returning Google users are no-ops.
  const maybeSeedGoogleOnboarding = async (signedInSession: Session): Promise<void> => {
    try {
      const provider = signedInSession.user?.app_metadata?.provider;
      if (provider !== 'google') return;

      // CRITICAL — distinguish a brand-new Google user from a returning one.
      // A returning user has already completed onboarding (interests / gender /
      // sponsorship set) and must go STRAIGHT HOME, never back through the
      // funnel. Only a new user (bare profile from the DB trigger) gets seeded.
      // We deliberately do NOT set `onboardingInProgress` here: that flag makes
      // AuthGuard jump to `onboarding-success`, which would skip the funnel that
      // actually collects this user's profile data. New-user routing into
      // `onboarding-welcome` is driven explicitly from `welcome.tsx`.
      let alreadyOnboarded = false;
      try {
        const { profileService } = await import('@/services/profileService');
        // Strict variant THROWS on a transient profile-fetch error (the normal
        // checkOnboardingStatus coerces errors to `false`, which would
        // misclassify a returning user as new).
        alreadyOnboarded = await profileService.getOnboardingCompletionStrict();
      } catch (statusErr) {
        // Fail safe: if we can't classify an existing session, do NOT seed —
        // forcing a returning user back through onboarding is the worse outcome.
        console.warn('⚠️ AuthContext: Google onboarding status check failed, skipping seed:', statusErr instanceof Error ? statusErr.message : String(statusErr));
        return;
      }
      if (alreadyOnboarded) {
        console.log('🔵 AuthContext: returning Google user — no onboarding seed');
        return;
      }

      // Guarantee a clinic assignment for the freshly created Google account.
      // The Supabase auth trigger creates a bare profile row with no clinic_id;
      // google/complete-profile only sets it at the END of onboarding, so a user
      // who abandons the funnel would be left with a NULL clinic. Bootstrap is
      // idempotent and assigns the default clinic (1) when missing. Non-blocking —
      // never break sign-in over this.
      try {
        const clinicRes = await fetch('https://mobile.padrinho.app/api/auth/profile/bootstrap', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${signedInSession.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ invitation_source: 'mobile_app_google' }),
        });
        if (clinicRes.ok) {
          console.log('🏥 AuthContext: Google clinic bootstrap ensured (default clinic)');
        } else {
          console.warn('⚠️ AuthContext: Google clinic bootstrap returned non-OK status:', clinicRes.status);
        }
      } catch (clinicErr) {
        console.warn('⚠️ AuthContext: Google clinic bootstrap failed (non-fatal):', clinicErr instanceof Error ? clinicErr.message : String(clinicErr));
      }

      const meta: any = signedInSession.user?.user_metadata || {};
      const googleName: string = meta.full_name ?? meta.name ?? '';

      const { TempOnboardingService } = await import('@/services/tempOnboardingService');

      // Only seed the name when there's no temp data yet, so we don't clobber a
      // name the user is mid-way through editing.
      const hasTemp = await TempOnboardingService.hasData();
      if (!hasTemp && googleName) {
        await TempOnboardingService.saveName(googleName);
      }

      console.log('🔵 AuthContext: Google onboarding seeded for new user');
    } catch (err) {
      console.warn('⚠️ AuthContext: maybeSeedGoogleOnboarding failed (non-fatal):', err instanceof Error ? err.message : String(err));
    }
  };

  // sessionOverride: Pass the session directly when calling right after setSession() 
  // (React state updates are async, so session state may not be updated yet)
  const loadEnhancedUserData = async (userId: string, sessionOverride?: Session | null): Promise<EnhancedUserData | null> => {
    try {
      console.log('🔍 AuthContext: Loading enhanced user data for:', userId);

      // CRITICAL: Get access token upfront to avoid calling supabase.auth.getSession() 
      // multiple times, which can hang on warm starts and block the UI
      let accessToken = sessionOverride?.access_token || session?.access_token;
      
      if (!accessToken) {
        console.log('⚠️ No session in state/override, trying getSession() with timeout...');
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise<null>((resolve) => 
          setTimeout(() => {
            console.warn('⚠️ getSession() timed out after 3s');
            resolve(null);
          }, 3000)
        );
        
        const result = await Promise.race([sessionPromise, timeoutPromise]);
        if (result && 'data' in result) {
          accessToken = result.data.session?.access_token;
        }
      }
      
      if (!accessToken) {
        console.error('❌ No active session found - cannot load enhanced user data');
        return null;
      }
      
      if (isTokenExpired(accessToken)) {
        console.log('⏰ Access token is expired, attempting refresh before API calls...');
        try {
          // T002 FIX: Always use SDK-managed refresh token (no args) — never pass
          // the app-cached refresh_token, which may be stale and trigger Supabase's
          // compromise detection, revoking all session tokens.
          const refreshResult = await Promise.race([
            supabase.auth.refreshSession(),
            new Promise<null>((resolve) => setTimeout(() => {
              console.warn('⚠️ Token refresh timed out after 5s');
              resolve(null);
            }, 5000))
          ]);
          
          if (refreshResult && 'data' in refreshResult) {
            const refreshedSession = (refreshResult as any).data?.session;
            if (refreshedSession?.access_token && !isTokenExpired(refreshedSession.access_token)) {
              console.log('✅ Token refreshed successfully before API calls');
              accessToken = refreshedSession.access_token;
              setAccessToken(refreshedSession.access_token);
              setSession(refreshedSession);
              cacheSessionLocally(refreshedSession);
            } else {
              console.warn('⚠️ Token refresh returned but token still invalid, skipping API calls');
              return null;
            }
          } else {
            console.warn('⚠️ Token refresh failed or timed out, skipping API calls with expired token');
            return null;
          }
        } catch (refreshError) {
          console.warn('⚠️ Token refresh error, skipping API calls:', refreshError);
          return null;
        }
      }
      
      console.log('✅ Access token available for enhanced data loading');

      // Load user profile with caching and timeout protection
      // Note: accessToken is already extracted at function start - no need to call getSession() again
      const profile = await cacheService.get(
        `user:profile:${userId}`,
        async () => {
          console.log('📊 Fetching user profile via secure API (bypassing RLS timing issues)');
          
          // Use secure API endpoint that bypasses RLS with service role
          // accessToken is already available from the function scope - no need to get it again
          try {
            console.log('📡 Calling API: https://mobile.padrinho.app/api/auth/profile/me');

            const profileTimeoutPromise = new Promise<never>((_, reject) => 
              setTimeout(() => reject(new Error('Profile fetch timeout after 10s')), 10000)
            );
            
            const profilePromise = fetch('https://mobile.padrinho.app/api/auth/profile/me', {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
            });
            
            console.log('⏳ Waiting for API response...');
            const response = await Promise.race([profilePromise, profileTimeoutPromise]) as Response;
            console.log('📥 API response received:', response.status, response.statusText);
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.error('❌ API returned error:', response.status, errorData);
              throw new Error(errorData.error || `API error: ${response.status}`);
            }
            
            console.log('✅ API response OK, parsing JSON...');
            const result = await response.json();
            console.log('📦 API result:', { success: result.success, hasProfile: !!result.profile });
            
            if (!result.success || !result.profile) {
              console.error('❌ Invalid API response structure');
              throw new Error('Profile not found in API response');
            }
            
            console.log('✅ Profile fetched successfully via secure API:', result.profile.id);
            return result.profile as UserProfile;
          } catch (apiError) {
            console.error('❌ Secure API fetch failed:', apiError);
            
            // Retry API once before giving up (no Supabase fallback to avoid RLS timing issues)
            console.log('🔄 Retrying API call...');
            try {
              const retryResponse = await fetch('https://mobile.padrinho.app/api/auth/profile/me', {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json',
                },
              });
              
              if (retryResponse.ok) {
                const retryResult = await retryResponse.json();
                if (retryResult.success && retryResult.profile) {
                  console.log('✅ Retry successful, profile fetched:', retryResult.profile.id);
                  return retryResult.profile as UserProfile;
                }
              }
            } catch (retryError) {
              console.error('❌ Retry also failed:', retryError);
            }
            
            // Return null instead of falling back to Supabase (which causes RLS timeout issues)
            console.error('❌ All API attempts failed, returning null');
            return null;
          }
        },
        cacheService.getConfig('USER')
      );

      console.log('📦 AuthContext: Profile fetch result:', {
        hasProfile: !!profile,
        profileId: profile?.id,
        fullName: profile?.full_name,
        clinicId: profile?.clinic_id
      });

      if (!profile) {
        console.warn('⚠️ No user profile found, attempting to create via bootstrap API...');
        
        // Auto-create profile via API (bypasses RLS timing issues)
        try {
          console.log('🔧 Creating missing profile via bootstrap API for user:', userId);
          
          const bootstrapResponse = await fetch('https://mobile.padrinho.app/api/auth/profile/bootstrap', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              invitation_source: 'mobile_app_direct_recovery'
            }),
          });
          
          if (bootstrapResponse.ok) {
            const bootstrapResult = await bootstrapResponse.json();
            if (bootstrapResult.success && bootstrapResult.profile) {
              console.log('✅ Recovery profile created successfully via API:', bootstrapResult.profile.id);
              
              // Clear cache and use the newly created profile
              await cacheService.invalidate(`user:profile:${userId}`);
              return {
                profile: bootstrapResult.profile as UserProfile,
                clinic: null,
                plan: null
              };
            }
          }
          
          console.error('❌ Bootstrap API failed:', await bootstrapResponse.text());
        } catch (recoveryError) {
          console.error('❌ Profile recovery via API failed:', recoveryError);
        }
        
        return null;
      }

      // Load clinic data and plan data in parallel for better performance
      // CRITICAL: Use profile.clinic_id directly instead of getUserClinicData() to avoid
      // circular cache dependency that causes promise to hang indefinitely
      // CRITICAL: Pass accessToken to getClinicData to avoid supabase.auth.getSession() hanging
      console.log('🏥 AuthContext: Starting clinic/plan fetch for clinic_id:', profile.clinic_id);
      const [clinicResult, planResult] = await Promise.allSettled([
        profile.clinic_id ? clinicCacheService.getClinicData(profile.clinic_id, accessToken) : Promise.resolve(null),
        clinicCacheService.getUserPlan(userId)
      ]);

      // Extract results, handling failures gracefully
      const clinic = clinicResult.status === 'fulfilled' ? clinicResult.value : null;
      const plan = planResult.status === 'fulfilled' ? planResult.value : null;

      // Log any failures without blocking the user experience
      if (clinicResult.status === 'rejected') {
        console.error('⚠️ Failed to load clinic data:', clinicResult.reason);
      }
      if (planResult.status === 'rejected') {
        console.error('⚠️ Failed to load plan data:', planResult.reason);
      }

      console.log('✅ AuthContext: Enhanced user data loaded:', {
        profileId: profile.id,
        fullName: profile.full_name,
        clinicId: profile.clinic_id,
        invitationSource: profile.invitation_source,
        hasProfile: !!profile,
        hasClinic: !!clinic,
        hasPlan: !!plan,
        clinicName: clinic?.name
      });

      return {
        profile,
        clinic,
        plan
      };
    } catch (error) {
      console.error('❌ AuthContext: Exception loading enhanced user data:', error);
      return null;
    }
  };

  // Legacy function for backward compatibility
  const loadUserProfile = async (userId: string, sessionOverride?: Session | null): Promise<UserProfile | null> => {
    const enhancedData = await loadEnhancedUserData(userId, sessionOverride || session);
    return enhancedData?.profile || null;
  };

  // Update enhanced user state safely
  const updateEnhancedUserState = async (enhancedData: EnhancedUserData | null) => {
    try {
      if (enhancedData) {
        setUserProfile(enhancedData.profile);
        setClinicData(enhancedData.clinic);
        setPlanData(enhancedData.plan);

        // Cache only non-sensitive profile data for offline access (legacy compatibility)
        const publicProfile = {
          id: enhancedData.profile.id,
          full_name: enhancedData.profile.full_name,
          avatar_url: enhancedData.profile.avatar_url,
          created_at: enhancedData.profile.created_at,
          updated_at: enhancedData.profile.updated_at
          // Exclude email and phone for security
        };
        await AsyncStorage.setItem('userProfile', JSON.stringify(publicProfile));

        // Persist a lightweight "last user" snapshot (intentionally NOT cleared
        // on sign-out) so the login screen can greet a returning user with
        // their name/avatar instead of a blank email field.
        try {
          const { lastUserService } = await import('@/services/lastUserService');
          await lastUserService.set({
            email: enhancedData.profile.email,
            name: enhancedData.profile.full_name ?? enhancedData.profile.name ?? null,
            avatarUrl: enhancedData.profile.avatar_url ?? null,
          });
        } catch (lastUserError) {
          console.warn('⚠️ AuthContext: failed to persist last user snapshot', lastUserError);
        }

        console.log('✅ AuthContext: Enhanced user state updated successfully');
      } else {
        setUserProfile(null);
        setClinicData(null);
        setPlanData(null);
        await AsyncStorage.removeItem('userProfile');
      }
    } catch (error) {
      console.error('❌ AuthContext: Error updating enhanced user state:', error);
    }
  };

  // Legacy function for backward compatibility
  const updateUserProfileState = async (profile: UserProfile | null) => {
    if (profile) {
      await updateEnhancedUserState({
        profile,
        clinic: clinicData,
        plan: planData
      });
    } else {
      await updateEnhancedUserState(null);
    }
  };

  // Load cached profile from AsyncStorage
  const loadCachedProfile = async (): Promise<UserProfile | null> => {
    try {
      const cached = await AsyncStorage.getItem('userProfile');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('❌ AuthContext: Error loading cached profile:', error);
    }
    return null;
  };

  // Retry initialization function
  const retryInitialization = async () => {
    console.log('🔄 AuthContext: Retrying initialization...');
    setError(null);
    setLoading(true);
    setRetryCount(prevCount => prevCount + 1);

    await initializeAuth();
  };

  // Clear error function
  const clearError = () => {
    setError(null);
  };

  // Enhanced error categorization
  const categorizeError = (error: any): { type: string; message: string; canRetry: boolean } => {
    const errorMessage = error?.message || 'Erro desconhecido';
    const lowerMessage = errorMessage.toLowerCase();

    // Network/connection errors
    if (lowerMessage.includes('fetch') || lowerMessage.includes('network') || lowerMessage.includes('timeout')) {
      return {
        type: 'network',
        message: 'Problema de conexão. Verifique sua internet.',
        canRetry: true
      };
    }

    // Storage/compatibility errors
    if (lowerMessage.includes('window is not defined') || lowerMessage.includes('localstorage')) {
      return {
        type: 'compatibility',
        message: 'Problema de compatibilidade. Recarregando...',
        canRetry: true
      };
    }

    // Auth-specific errors
    if (lowerMessage.includes('invalid session') || lowerMessage.includes('jwt')) {
      return {
        type: 'session',
        message: 'Sessão expirada. Faça login novamente.',
        canRetry: false
      };
    }

    // Server errors
    if (lowerMessage.includes('500') || lowerMessage.includes('internal server error')) {
      return {
        type: 'server',
        message: 'Erro no servidor. Tente novamente em alguns minutos.',
        canRetry: true
      };
    }

    return {
      type: 'unknown',
      message: 'Erro inesperado. Tente novamente.',
      canRetry: true
    };
  };



  // Check environment compatibility with enhanced validation
  const checkEnvironmentCompatibility = async (): Promise<boolean> => {
    try {
      // Test Supabase connection first
      await validateSupabaseConnection();

      // Test basic storage functionality
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error('❌ AuthContext: Session check failed:', error.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ AuthContext: Environment compatibility check failed:', error);
      return false;
    }
  };

  // Enhanced profile loading with retry logic
  const loadProfileWithRetry = async (userId: string) => {
    let profileRetries = 0;
    const maxProfileRetries = 3;

    while (profileRetries < maxProfileRetries) {
      try {
        profileRetries++;
        console.log(`🔄 AuthContext: Profile load attempt ${profileRetries}/${maxProfileRetries}`);

        const freshProfile = await loadUserProfile(userId);
        if (freshProfile) {
          await updateUserProfileState(freshProfile);
          console.log('✅ AuthContext: Profile loaded successfully');
          return;
        } else {
          throw new Error('Profile data is null');
        }
      } catch (profileError) {
        console.warn(`❌ AuthContext: Profile load attempt ${profileRetries} failed:`, profileError);

        if (profileRetries >= maxProfileRetries) {
          console.error('❌ AuthContext: All profile load attempts failed');
          const errorInfo = categorizeError(profileError);
          setError(`Erro ao carregar perfil: ${errorInfo.message}`);
        } else {
          // Progressive delay: 1s, 2s, 3s
          await new Promise(resolve => setTimeout(resolve, 1000 * profileRetries));
        }
      }
    }
  };

  // SESSION CACHE KEYS for instant cold start restoration
  const SESSION_CACHE_KEY = 'cached_session_data';
  const SESSION_CACHE_TIMESTAMP_KEY = 'cached_session_timestamp';
  // Task #60: Raised from 7 days → 365 days to fix "logged off after a few days"
  // for casual users. The Supabase project has Time-box=0 and Inactivity=0, so
  // refresh tokens never expire on their own — the cache should not expire
  // BEFORE the refresh token does. Setting this to 365 days aligns the local
  // cache with the "open it whenever you need" promise of the recovery
  // companion. Sessions still end on explicit logout, password change,
  // server-side revocation, or genuine refresh-token compromise.
  const SESSION_CACHE_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 365 days

  // Cache session locally for instant cold start
  const cacheSessionLocally = async (session: Session | null) => {
    try {
      if (session) {
        await AsyncStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          user: {
            id: session.user.id,
            email: session.user.email,
            created_at: session.user.created_at,
          }
        }));
        await AsyncStorage.setItem(SESSION_CACHE_TIMESTAMP_KEY, Date.now().toString());
        console.log('💾 AuthContext: Session cached locally for fast cold start');
      } else {
        await AsyncStorage.removeItem(SESSION_CACHE_KEY);
        await AsyncStorage.removeItem(SESSION_CACHE_TIMESTAMP_KEY);
        console.log('🗑️ AuthContext: Cleared local session cache');
      }
    } catch (error) {
      console.warn('⚠️ AuthContext: Failed to cache session locally:', error);
    }
  };

  // Restore session from local cache (instant, no network)
  // CRITICAL: Returns BOTH user AND session with tokens for API calls
  const restoreCachedSession = async (): Promise<{ user: User | null; session: Session | null; needsValidation: boolean }> => {
    try {
      const cachedData = await AsyncStorage.getItem(SESSION_CACHE_KEY);
      const cachedTimestamp = await AsyncStorage.getItem(SESSION_CACHE_TIMESTAMP_KEY);
      
      if (!cachedData || !cachedTimestamp) {
        console.log('ℹ️ AuthContext: No cached session found');
        return { user: null, session: null, needsValidation: false };
      }
      
      const cacheAge = Date.now() - parseInt(cachedTimestamp, 10);
      if (cacheAge > SESSION_CACHE_MAX_AGE_MS) {
        console.log('⏰ AuthContext: Cached session expired, clearing...');
        await AsyncStorage.removeItem(SESSION_CACHE_KEY);
        await AsyncStorage.removeItem(SESSION_CACHE_TIMESTAMP_KEY);
        return { user: null, session: null, needsValidation: false };
      }
      
      const parsed = JSON.parse(cachedData);
      if (parsed?.user?.id && parsed?.user?.email && parsed?.access_token) {
        console.log('⚡ AuthContext: Restored session from local cache (instant):', parsed.user.email);
        
        // Reconstruct a partial session object with the cached tokens
        // This allows API calls to work immediately without waiting for getSession()
        const cachedSession: Session = {
          access_token: parsed.access_token,
          refresh_token: parsed.refresh_token || '',
          token_type: 'bearer',
          expires_in: 3600, // Default, will be validated later
          expires_at: Math.floor(Date.now() / 1000) + 3600, // Assume valid for 1 hour
          user: parsed.user as User,
        };
        
        return { 
          user: parsed.user as User, 
          session: cachedSession,
          needsValidation: true 
        };
      }
      
      return { user: null, session: null, needsValidation: false };
    } catch (error) {
      console.warn('⚠️ AuthContext: Failed to restore cached session:', error);
      return { user: null, session: null, needsValidation: false };
    }
  };

  // Initialize auth state with LOCAL-FIRST approach for fast cold starts
  const initializeAuth = async () => {
    let mounted = true;
    let authSubscription: any = null;

    const runInitialization = async () => {
      if (!mounted) return;

      console.log('🔧 AuthContext: Starting initialization (local-first approach)...');
      const startTime = Date.now();

      // STEP 1: Try to restore from local cache FIRST (instant, no network)
      // CRITICAL: Now also restores session with access_token for API calls
      const { user: cachedUser, session: cachedSession, needsValidation } = await restoreCachedSession();
      
      if (cachedUser) {
        console.log('⚡ AuthContext: Using cached user for instant UI render');
        setUser(cachedUser);
        
        // CRITICAL FIX: Also set the cached session so API calls work immediately
        if (cachedSession) {
          console.log('⚡ AuthContext: Using cached session with access_token for API calls');
          
          // CRITICAL: Set the token in apiService SYNCHRONOUSLY before setting sessionReady
          // This ensures the token is available before contexts try to make API calls
          if (cachedSession.access_token) {
            setAccessToken(cachedSession.access_token);
            console.log('🔑 AuthContext: Token synced to apiService synchronously');
          }
          
          setSession(cachedSession);
          // CRITICAL: Set sessionReady=true immediately to allow contexts to load data
          // API calls will handle 401s and refresh tokens automatically via apiService
          // This is the market best practice: trust cached session, validate lazily on API call
          setSessionReady(true);
          console.log('✅ AuthContext: sessionReady=true (trusting cached session, lazy validation)');
        }
        
        // Load cached profile for instant display
        const cachedProfile = await loadCachedProfile();
        if (cachedProfile) {
          console.log('⚡ AuthContext: Using cached profile for instant UI render');
          setUserProfile(cachedProfile);
        }
        
        // CRITICAL: Start loading enhanced data immediately with cached session
        // Don't wait for background validation - API calls will refresh token if needed
        if (cachedSession?.access_token) {
          console.log('🚀 AuthContext: Loading enhanced data immediately with cached token');

          loadEnhancedUserData(cachedUser.id, cachedSession)
            .then(enhancedData => {
              if (enhancedData) {
                updateEnhancedUserState(enhancedData);
              }
            })
            .catch(err => {
              console.warn('⚠️ AuthContext: loadEnhancedUserData failed (cached session path):', err);
            });
        }
        
        // Mark as ready for UI - loading complete in <100ms typically
        const cacheRestoreTime = Date.now() - startTime;
        console.log(`✅ AuthContext: Local cache restored in ${cacheRestoreTime}ms - UI ready!`);
        setLoading(false);
        setIsInitializing(false);
      }

      // STEP 2: Validate/refresh session with Supabase (in background if we have cached user)
      // This is non-critical when we have cached session - just updates tokens if possible
      // CRITICAL FIX: If no cached user/session exists, skip server validation entirely
      // There's nothing to validate - user needs to log in
      if (!cachedUser && !cachedSession) {
        console.log('✅ AuthContext: No cached session - skipping server validation, showing welcome screen');
        setLoading(false);
        setIsInitializing(false);
        setSessionReady(true); // Allow app to render (no user = welcome screen)
        
        // Still set up auth state listener for when user logs in
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event: AuthChangeEvent, session: Session | null) => {
            console.log('🔐 AuthContext: Auth state changed:', event, session?.user?.email || 'no user');
            if (!mounted) return;
            
            if (event === 'SIGNED_IN' && session?.user) {
              console.log('✅ AuthContext: User signed in:', session.user.email);
              // CRITICAL: Set token in apiService BEFORE sessionReady to prevent race condition
              if (session.access_token) {
                setAccessToken(session.access_token);
              }
              setSession(session);
              setSessionReady(true);
              setUser(session.user);
              setError(null);

              // Task #60: Unblock routing immediately. AuthGuard navigates as
              // soon as `user` is set; never gate that on profile load.
              setLoading(false);

              await cacheSessionLocally(session);

              // Keep any enabled biometric credentials bound to the account that
              // just signed in. updateStoredSession is a no-op when biometrics
              // are disabled, so this also stops a previous user's stored session
              // from lingering after an account switch on a shared device.
              try {
                await biometricService.updateStoredSession(session.access_token, session.refresh_token);
              } catch (bioSyncErr) {
                console.warn('⚠️ AuthContext: Biometric token sync failed (SIGNED_IN, no-cache path):', bioSyncErr);
              }

              // PRD_GOOGLE_AUTH §3 — seed onboarding for a new Google user.
              await maybeSeedGoogleOnboarding(session);

              // Register user with OneSignal SDK (non-blocking)
              // SDK's login() handles user creation and external_id linking automatically
              (async () => {
                try {
                  await oneSignalService.setExternalUserId(session.user.id);
                  console.log('✅ AuthContext: OneSignal user linked (SIGNED_IN event)');
                } catch (err) {
                  console.warn('⚠️ AuthContext: OneSignal login error (SIGNED_IN event):', err);
                }
              })();

              // Consent and biometric checks are now handled by StartupPromptContext

              // Load enhanced user data in the BACKGROUND — never block routing.
              loadEnhancedUserData(session.user.id, session)
                .then((enhancedData) => {
                  if (enhancedData && mounted) {
                    updateEnhancedUserState(enhancedData);
                  }
                })
                .catch((err) => {
                  console.error('❌ AuthContext: Failed to load profile (background, no-cache path):', err);
                });
            } else if (event === 'TOKEN_REFRESHED' && session?.user) {
              // Biometric setSession() with an expired access_token causes Supabase to
              // auto-refresh using the stored refresh_token and emit TOKEN_REFRESHED
              // instead of SIGNED_IN. Without this branch the user is stuck on the
              // login screen even though Face ID succeeded.
              console.log('🔄 AuthContext: Token refreshed (no-cache path — biometric restore)');
              if (session.access_token) {
                setAccessToken(session.access_token);
              }
              setSession(session);
              setSessionReady(true);
              setUser(session.user);
              setError(null);
              setLoading(false);
              setIsInitializing(false);

              await cacheSessionLocally(session);

              // Sync new tokens back to SecureStore so next biometric login uses fresh tokens
              try {
                await biometricService.updateStoredSession(session.access_token, session.refresh_token);
              } catch (bioSyncErr) {
                console.warn('⚠️ AuthContext: Biometric token sync failed (TOKEN_REFRESHED, no-cache path):', bioSyncErr);
              }

              // Register with OneSignal (non-blocking)
              (async () => {
                try {
                  await oneSignalService.setExternalUserId(session.user.id);
                  console.log('✅ AuthContext: OneSignal user linked (TOKEN_REFRESHED, no-cache path)');
                } catch (err) {
                  console.warn('⚠️ AuthContext: OneSignal login error (TOKEN_REFRESHED, no-cache path):', err);
                }
              })();

              // Load enhanced user data in the background — never block routing
              loadEnhancedUserData(session.user.id, session)
                .then((enhancedData) => {
                  if (enhancedData && mounted) {
                    updateEnhancedUserState(enhancedData);
                  }
                })
                .catch((err) => {
                  console.error('❌ AuthContext: Failed to load profile (TOKEN_REFRESHED, no-cache path):', err);
                });
            } else if (event === 'SIGNED_OUT') {
              console.log('🔒 AuthContext: User signed out');
              setSession(null);
              setSessionReady(true);
              setUser(null);
              setUserProfile(null);
              setClinicData(null);
              await cacheSessionLocally(null);
            }
          }
        );
        
        authSubscription = subscription;
        return; // Exit early - no need to validate anything
      }
      
      if (cachedSession) {
        console.log('🔄 AuthContext: Background session refresh (non-blocking)...');
      } else {
        console.log('🌐 AuthContext: Validating session with server...');
      }
      
      let sessionRestored = false;
      let lastError: any = null;
      // Increased timeout - refreshSession is critical for getting valid tokens
      const TIMEOUT_MS = cachedSession ? 8000 : 10000;
      
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Session validation timeout')), TIMEOUT_MS)
        );

        // T002 FIX: Always use getSession() — never pass cachedSession.refresh_token.
        // The SDK's internal storage always holds the latest refresh token after rotation.
        // Passing the app-cached token risks triggering Supabase's compromise detection
        // (reuse interval = 10s), which revokes ALL tokens for the session.
        const sessionPromise = supabase.auth.getSession();
        const result: any = await Promise.race([sessionPromise, timeoutPromise]);

        const { data: { session }, error: sessionError } = result;

        if (sessionError) {
          console.warn('⚠️ AuthContext: Session validation error:', sessionError.message);
          lastError = sessionError;
          
          // NEVER auto-logout on errors - always try to use cached session
          // Users should only be logged out when they explicitly tap Sign Out
          if (cachedSession?.access_token && cachedUser) {
            console.log('✅ AuthContext: Using cached session despite error (never auto-logout)');
            
            let tokenToUse = cachedSession.access_token;
            let sessionToUse = cachedSession;
            if (isTokenExpired(cachedSession.access_token)) {
              console.log('⏰ Cached token expired, trying refresh before use...');
              try {
                // T002 FIX: No args — use SDK-managed refresh token only
                const refreshResult = await Promise.race([
                  supabase.auth.refreshSession(),
                  new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000))
                ]);
                if (refreshResult && 'data' in refreshResult) {
                  const refreshed = (refreshResult as any).data?.session;
                  if (refreshed?.access_token && !isTokenExpired(refreshed.access_token)) {
                    console.log('✅ Token refreshed in error fallback path');
                    tokenToUse = refreshed.access_token;
                    sessionToUse = refreshed;
                    await cacheSessionLocally(refreshed);
                  }
                }
              } catch (e) {
                console.warn('⚠️ Token refresh failed in error fallback:', e);
              }
            }
            
            setAccessToken(tokenToUse);
            setSession(sessionToUse);
            setSessionReady(true);
            
            // Register user with OneSignal SDK (non-blocking)
            // SDK's login() handles user creation and external_id linking automatically
            (async () => {
              try {
                await oneSignalService.setExternalUserId(cachedUser.id);
                console.log('✅ AuthContext: OneSignal user linked (cached session path)');
              } catch (err) {
                console.warn('⚠️ AuthContext: OneSignal login error (cached session path):', err);
              }
            })();
            
            // Load fresh user data in background
            if (cachedUser?.id) {
              console.log('🔄 AuthContext: Loading user data after session error fallback...');
              loadEnhancedUserData(cachedUser.id, sessionToUse)
                .then(enhancedData => {
                  if (enhancedData) {
                    updateEnhancedUserState(enhancedData);
                  }
                })
                .catch(err => {
                  console.error('❌ AuthContext: loadEnhancedUserData failed (session error path):', err);
                });
            }
            sessionRestored = true;
          }
        } else if (session?.user) {
          console.log('✅ AuthContext: Session validated with server:', session.user.email);

          // T004 FIX: Race condition guard — only set session if it's newer than what
          // TOKEN_REFRESHED may have already set. TOKEN_REFRESHED fires during getSession()
          // if the SDK refreshed internally; we must not overwrite its fresher tokens.
          const sessionIat = getTokenIat(session.access_token);
          if (sessionIat < latestSessionIat.current) {
            console.log('⏭️ AuthContext: Skipping init session set — TOKEN_REFRESHED already applied a newer session');
            sessionRestored = true;
          } else {
            latestSessionIat.current = sessionIat;

            // CRITICAL: Set token in apiService BEFORE sessionReady to prevent race condition
            if (session.access_token) {
              setAccessToken(session.access_token);
            }
            setSession(session);
            setUser(session.user);
            setSessionReady(true); // Session is validated and safe for API calls
            sessionRestored = true;

            // Cache the validated session for next cold start
            await cacheSessionLocally(session);
          }

          // Register user with OneSignal SDK (non-blocking)
          // SDK's login() handles user creation and external_id linking automatically
          (async () => {
            try {
              await oneSignalService.setExternalUserId(session.user.id);
              console.log('✅ AuthContext: OneSignal user linked (new session)');
            } catch (err) {
              console.warn('⚠️ AuthContext: OneSignal login error:', err);
            }
          })();

          loadEnhancedUserData(session.user.id, session)
            .then(enhancedData => {
              if (enhancedData) {
                updateEnhancedUserState(enhancedData);
              } else {
                console.warn('⚠️ AuthContext: loadEnhancedUserData returned null (session validated path)');
              }
            })
            .catch(err => {
              console.error('❌ AuthContext: loadEnhancedUserData failed (session validated path):', err);
            });
        } else {
          console.log('ℹ️ AuthContext: No server session found');
          // NEVER auto-logout - if we have cached session, use it
          if (cachedSession?.access_token && cachedUser) {
            console.log('✅ AuthContext: No server session but using cached session (never auto-logout)');
            
            let tokenToUse = cachedSession.access_token;
            let sessionToUse = cachedSession;
            if (isTokenExpired(cachedSession.access_token)) {
              console.log('⏰ Cached token expired (no server session), trying refresh...');
              try {
                // T002 FIX: No args — use SDK-managed refresh token only
                const refreshResult = await Promise.race([
                  supabase.auth.refreshSession(),
                  new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000))
                ]);
                if (refreshResult && 'data' in refreshResult) {
                  const refreshed = (refreshResult as any).data?.session;
                  if (refreshed?.access_token && !isTokenExpired(refreshed.access_token)) {
                    console.log('✅ Token refreshed (no server session path)');
                    tokenToUse = refreshed.access_token;
                    sessionToUse = refreshed;
                    await cacheSessionLocally(refreshed);
                  }
                }
              } catch (e) {
                console.warn('⚠️ Token refresh failed (no server session):', e);
              }
            }
            
            setAccessToken(tokenToUse);
            setSession(sessionToUse);
            setSessionReady(true);
            sessionRestored = true;
            
            // Register user with OneSignal SDK (non-blocking)
            // SDK's login() handles user creation and external_id linking automatically
            (async () => {
              try {
                await oneSignalService.setExternalUserId(cachedUser.id);
                console.log('✅ AuthContext: OneSignal user linked (no server session path)');
              } catch (err) {
                console.warn('⚠️ AuthContext: OneSignal login error (no server session path):', err);
              }
            })();
          } else {
            // No cached session either - this is a fresh install or explicit logout
            console.log('ℹ️ AuthContext: No cached session available');
            sessionRestored = true;
          }
        }
      } catch (error: any) {
        lastError = error;
        
        // NEVER auto-logout on errors - always use cached session if available
        if (cachedSession?.access_token && cachedUser) {
          console.log('✅ AuthContext: Using cached session despite error (never auto-logout)');
          
          let tokenToUse = cachedSession.access_token;
          let sessionToUse = cachedSession;
          if (isTokenExpired(cachedSession.access_token)) {
            console.log('⏰ Cached token expired in catch path, trying refresh...');
            try {
              // T002 FIX: No args — use SDK-managed refresh token only
              const refreshResult = await Promise.race([
                supabase.auth.refreshSession(),
                new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000))
              ]);
              if (refreshResult && 'data' in refreshResult) {
                const refreshed = (refreshResult as any).data?.session;
                if (refreshed?.access_token && !isTokenExpired(refreshed.access_token)) {
                  console.log('✅ Token refreshed in catch fallback path');
                  tokenToUse = refreshed.access_token;
                  sessionToUse = refreshed;
                  await cacheSessionLocally(refreshed);
                }
              }
            } catch (e) {
              console.warn('⚠️ Token refresh failed in catch fallback:', e);
            }
          }
          
          setAccessToken(tokenToUse);
          setSession(sessionToUse);
          setSessionReady(true);
          sessionRestored = true;
          
          // Register user with OneSignal SDK (non-blocking)
          // SDK's login() handles user creation and external_id linking automatically
          (async () => {
            try {
              await oneSignalService.setExternalUserId(cachedUser.id);
              console.log('✅ AuthContext: OneSignal user linked (catch fallback path)');
            } catch (err) {
              console.warn('⚠️ AuthContext: OneSignal login error (catch fallback path):', err);
            }
          })();
          
          // Load fresh user data in background
          if (cachedUser?.id) {
            console.log('🔄 AuthContext: Loading user data after error fallback...');
            loadEnhancedUserData(cachedUser.id, sessionToUse)
              .then(enhancedData => {
                if (enhancedData) {
                  updateEnhancedUserState(enhancedData);
                }
              })
              .catch(err => {
                console.error('❌ AuthContext: loadEnhancedUserData failed (error fallback path):', err);
              });
          }
        } else if (cachedUser) {
          console.warn('⚠️ AuthContext: Error during validation, no cached session available');
          sessionRestored = true;
        } else {
          console.warn('❌ AuthContext: Session validation failed:', error.message);
        }
      }

      const totalTime = Date.now() - startTime;
      console.log(`⏱️ AuthContext: Full initialization took ${totalTime}ms`);

      // Finalize initialization state (only if we didn't already from cache)
      if (!cachedUser) {
        if (!sessionRestored) {
          console.error('❌ AuthContext: Session validation failed:', lastError?.message);
          setError('Problema ao conectar. Verifique sua internet e tente novamente.');
        } else {
          console.log('✅ AuthContext: Initialization complete (no cached user)');
        }
        setLoading(false);
        setIsInitializing(false);
      }

      // Set up auth state listener
      try {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event: AuthChangeEvent, session: Session | null) => {
            console.log('🔐 AuthContext: Auth state changed:', event, session?.user?.email || 'no user');

            if (!mounted) return;

            try {
              if (event === 'SIGNED_IN' && session?.user) {
                console.log('✅ AuthContext: User signed in:', session.user.email);
                // CRITICAL: Set token in apiService BEFORE sessionReady to prevent race condition
                if (session.access_token) {
                  setAccessToken(session.access_token);
                }
                setSession(session);
                setSessionReady(true); // Fresh session from sign-in is validated
                setUser(session.user);
                setError(null);

                // Task #60: Unblock routing immediately. AuthGuard owns navigation
                // and was previously blocked while enhancedLoading was true (up to
                // 10s after SIGNED_IN), trapping users on /login after a successful
                // biometric prompt. Mirror the cached-session path: clear loading
                // flags as soon as the session is set, then load the profile in
                // the background. AuthGuard navigates as soon as `user` is set;
                // tab screens render their own loading states for profile data.
                setLoading(false);
                setIsInitializing(false);

                // Cache session for fast cold starts
                await cacheSessionLocally(session);

                // Keep any enabled biometric credentials bound to the account
                // that just signed in. updateStoredSession is a no-op when
                // biometrics are disabled, so this also stops a previous user's
                // stored session from lingering after an account switch on a
                // shared device.
                try {
                  await biometricService.updateStoredSession(session.access_token, session.refresh_token);
                } catch (bioSyncErr) {
                  console.warn('⚠️ AuthContext: Biometric token sync failed (SIGNED_IN, alt path):', bioSyncErr);
                }

                // PRD_GOOGLE_AUTH §3 — seed onboarding for a new Google user.
                await maybeSeedGoogleOnboarding(session);

                // Register user with OneSignal SDK (non-blocking)
                // SDK's login() handles user creation and external_id linking automatically
                (async () => {
                  try {
                    await oneSignalService.setExternalUserId(session.user.id);
                    console.log('✅ AuthContext: OneSignal user linked (SIGNED_IN event alt path)');
                  } catch (err) {
                    console.warn('⚠️ AuthContext: OneSignal login error (SIGNED_IN event alt path):', err);
                  }
                })();

                // Load enhanced user data in the BACKGROUND — never block routing.
                console.log('🔄 AuthContext: Loading enhanced user data in background after sign in');
                loadEnhancedUserData(session.user.id, session)
                  .then((enhancedData) => {
                    if (enhancedData && mounted) {
                      updateEnhancedUserState(enhancedData);
                      console.log('✅ AuthContext: Enhanced user data loaded (background)', {
                        profileId: enhancedData.profile?.id,
                        fullName: enhancedData.profile?.full_name,
                      });
                    }
                  })
                  .catch((profileError) => {
                    console.error('❌ AuthContext: Failed to load profile (background):', profileError);
                  });

                // Initialize badge manager (non-blocking)
                try {
                  const { default: BadgeManagerService } = await import('../services/badgeManagerService');
                  await BadgeManagerService.initialize(true);
                  console.log('📱 Badge manager initialized');
                } catch (badgeError) {
                  console.warn('⚠️ Badge manager initialization failed:', (badgeError as Error)?.message);
                }
              } else if (event === 'SIGNED_OUT') {
                console.log('ℹ️ AuthContext: User signed out');

                // T004 FIX: Clear OneSignal external_id on server-triggered sign-out.
                // When Supabase revokes a session (compromise detection, admin action),
                // it fires SIGNED_OUT but signOut() is never called — so without this,
                // OneSignal retains the stale external_id and keeps sending pushes to
                // the wrong (or deleted) user on this device.
                try {
                  await oneSignalService.logout();
                  console.log('✅ AuthContext: OneSignal cleared on SIGNED_OUT event');
                } catch (oneSignalError) {
                  console.warn('⚠️ AuthContext: OneSignal logout error on SIGNED_OUT:', oneSignalError);
                }

                // Reset iat tracking so next login starts fresh
                latestSessionIat.current = 0;

                setSession(null);
                setUser(null);
                setUserProfile(null);
                setClinicData(null);
                setPlanData(null);
                setError(null);
                await AsyncStorage.removeItem('userProfile');
                
                // Clear session cache for cold starts
                await cacheSessionLocally(null);
                
                // Clear caches
                try {
                  await cacheService.invalidatePattern('user:');
                  await clinicCacheService.invalidateAllClinicCaches();
                } catch (cacheError) {
                  console.warn('⚠️ Error clearing caches:', cacheError);
                }
              } else if (event === 'TOKEN_REFRESHED' && session?.user) {
                console.log('🔄 AuthContext: Token refreshed');

                // T004 FIX: TOKEN_REFRESHED always has the freshest tokens from the server.
                // Update latestSessionIat so the init code race condition guard knows
                // not to overwrite this session with an older one from getSession().
                const refreshedIat = getTokenIat(session.access_token);
                latestSessionIat.current = refreshedIat;

                setSession(session);
                setUser(session.user);
                setAccessToken(session.access_token);

                // Biometric setSession() with an expired access_token auto-refreshes
                // and emits TOKEN_REFRESHED (not SIGNED_IN). This is the common
                // biometric-restore path because the stored access token is usually
                // older than 1h. Mirror the SIGNED_IN handler and unblock routing:
                // AuthGuard renders a full-screen loading spinner whenever these
                // flags stay true, so without clearing them a successful Face ID /
                // Touch ID prompt leaves the user stuck behind a spinner forever.
                setSessionReady(true);
                setError(null);
                setLoading(false);
                setIsInitializing(false);

                // Update cached session with refreshed tokens
                await cacheSessionLocally(session);

                // Sync refreshed tokens to SecureStore if biometric is enabled
                try {
                  await biometricService.updateStoredSession(session.access_token, session.refresh_token);
                } catch (bioSyncError) {
                  console.warn('⚠️ AuthContext: Biometric token sync failed:', bioSyncError);
                }
              }
            } catch (error) {
              console.error('❌ AuthContext: Error handling auth state change:', error);
            }
          }
        );

        authSubscription = subscription;
      } catch (error) {
        console.error('❌ AuthContext: Error setting up auth listener:', error);
      }
    };

    runInitialization();

    return () => {
      mounted = false;
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  // T003 FIX: Conditional foreground session refresh
  // When the app returns to foreground, check if the access token is expired
  // and proactively refresh it. This works in tandem with T001 (AppState in
  // lib/supabase.ts) — T001 restarts the SDK auto-refresh timer, T003 is the
  // explicit safety net for tokens that already expired while backgrounded.
  // Rate-limit guard: skips refresh if one was done within the last 60 seconds.
  useEffect(() => {
    // Only needed on native platforms — web JS timers don't get suspended
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') return;

    const lastRefreshAt = { current: 0 }; // ms timestamp of last successful refresh
    const REFRESH_COOLDOWN_MS = 60 * 1000; // 60-second minimum between refresh calls

    const subscription = AppState.addEventListener('change', async (nextState) => {
      if (nextState !== 'active') return;
      if (!session?.access_token) return; // No session — nothing to refresh

      // Cooldown guard: don't hammer the endpoint on rapid app switches
      const now = Date.now();
      if (now - lastRefreshAt.current < REFRESH_COOLDOWN_MS) {
        console.log('⏭️ AuthContext: Skipping foreground refresh — cooldown active');
        return;
      }

      // Only refresh if the token is actually expired (T001 handles near-expiry proactively)
      if (!isTokenExpired(session.access_token)) return;

      console.log('🔄 AuthContext: Token expired on foreground resume — refreshing...');
      try {
        // T002-safe: no args — always uses SDK-managed refresh token
        const { data, error } = await supabase.auth.refreshSession();

        if (error) {
          console.warn('⚠️ AuthContext: Foreground session refresh failed:', error.message);
          return;
        }

        if (data?.session) {
          lastRefreshAt.current = Date.now();
          setSession(data.session);
          setUser(data.session.user);
          setAccessToken(data.session.access_token);
          await cacheSessionLocally(data.session);
          console.log('✅ AuthContext: Session refreshed on foreground resume');
        }
      } catch (err) {
        console.warn('⚠️ AuthContext: Foreground refresh error:', err);
      }
    });

    return () => {
      subscription.remove();
    };
  }, [session]); // Re-register when session changes so we always check the current token

  // Sign in
  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 AuthContext: Signing in user:', email);

      // Validate inputs
      if (!validateEmail(email)) {
        return { error: new Error('Email inválido') };
      }

      if (!password || password.length < 6) {
        return { error: new Error('Senha deve ter pelo menos 6 caracteres') };
      }

      // Enhanced connection test with progressive timeout and retry
      console.log('🔍 AuthContext: Testing connection...');
      let connectionAttempts = 0;
      const maxConnectionAttempts = 3;
      let connectionError = null;

      while (connectionAttempts < maxConnectionAttempts && !connectionError) {
        try {
          connectionAttempts++;
          console.log(`🔄 Connection attempt ${connectionAttempts}/${maxConnectionAttempts}`);

          // Progressive timeout: 3s, 5s, 8s
          const timeoutMs = 3000 + (connectionAttempts * 2000);

          // Use a simple connectivity test that doesn't require auth
          const connectionPromise = supabase.auth.getSession();

          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Connection timeout')), timeoutMs)
          );

          const result = await Promise.race([
            connectionPromise,
            timeoutPromise
          ]);

          const { error } = result as any;

          // For sign-in, we only care about connectivity, not session validity
          if (!error || error.message?.includes('session') || error.message?.includes('JWT')) {
            console.log('✅ Connection test successful - Supabase is reachable');
            connectionError = null;
            break;
          } else {
            connectionError = error;
            console.warn(`⚠️ Connection attempt ${connectionAttempts} failed:`, error.message);
          }
        } catch (attemptError) {
          connectionError = attemptError;
          console.warn(`⚠️ Connection attempt ${connectionAttempts} exception:`, attemptError);

          // Wait before retry with exponential backoff
          if (connectionAttempts < maxConnectionAttempts) {
            const delay = 500 * Math.pow(2, connectionAttempts - 1);
            console.log(`⏳ Waiting ${delay}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      if (connectionError) {
        console.error('❌ AuthContext: Connection test failed:', connectionError);

        // Categorize connection errors
        if (connectionError.message?.includes('fetch')) {
          return { error: new Error('Erro de rede. Verifique sua conexão com a internet.') };
        } else if (connectionError.message?.includes('Invalid API key')) {
          return { error: new Error('Erro de configuração. Tente novamente mais tarde.') };
        } else {
          return { error: new Error('Erro de conexão. Verifique sua internet.') };
        }
      }

      console.log('✅ AuthContext: Connection test successful');

      // Attempt sign in with timeout
      console.log('🔐 AuthContext: Attempting sign in...');
      try {
        const signInPromise = supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password,
        });

        const signInTimeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Sign in timeout')), 15000)
        );

        const signInResult = await Promise.race([
          signInPromise,
          signInTimeoutPromise
        ]) as any;

        const { error } = signInResult;

        if (error) {
          console.error('❌ AuthContext: Sign in error:', error);
          const userFriendlyError = getUserFriendlyError(error);
          return { error: new Error(userFriendlyError) };
        } else {
          console.log('✅ AuthContext: Sign in successful');
        }

        return { error: null };
      } catch (signInError: any) {
        console.error('❌ AuthContext: Sign in timeout or error:', signInError);
        if (signInError.message?.includes('timeout')) {
          return { error: new Error('Timeout durante o login. Tente novamente.') };
        }
        return { error: new Error('Erro durante o login. Tente novamente.') };
      }

    } catch (error) {
      console.error('❌ AuthContext: Exception during sign in:', error);
      return { error: new Error('Erro inesperado durante o login') };
    }
  };

  // Sign up
  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      // Validate inputs
      if (!validateEmail(email)) {
        return { error: new Error('Email inválido') };
      }

      if (password.length < 8) {
        return { error: new Error('A senha deve ter pelo menos 8 caracteres') };
      }

      const sanitizedName = sanitizeInput(fullName);
      if (sanitizedName.length < 2) {
        return { error: new Error('Nome deve ter pelo menos 2 caracteres') };
      }

      console.log('🔐 AuthContext: Signing up user:', email);

      // Write suppression flag BEFORE triggering the Supabase signup so it is
      // present when the SIGNED_IN event fires and StartupPromptContext builds
      // its queue — eliminates the race condition for fresh signups.
      try {
        // Kept for backward-compat with any old build still reading this
        // flag; the username prompt itself has been removed.
        await AsyncStorage.setItem(USERNAME_PROMPT_SKIP_KEY, 'true');
      } catch {
        // non-blocking
      }

      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            full_name: sanitizedName,
          },
        },
      });

      if (signUpError) {
        // Clean up the flag we pre-wrote since signup failed
        try { await AsyncStorage.removeItem(USERNAME_PROMPT_SKIP_KEY); } catch { /* non-blocking */ }
        console.error('❌ AuthContext: Sign up error:', signUpError);
        const userFriendlyError = getUserFriendlyError(signUpError);
        return { error: new Error(userFriendlyError) };
      }

      if (signUpData.user && signUpData.session) {
        console.log('✅ AuthContext: Sign up successful');

        // Use server-side API to create/verify profile (bypasses RLS timing issues)
        console.log('📡 AuthContext: Calling bootstrap API to create profile...');
        try {
          const bootstrapResponse = await fetch('https://mobile.padrinho.app/api/auth/profile/bootstrap', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${signUpData.session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              full_name: sanitizedName,
              email: signUpData.user.email,
            }),
          });

          const bootstrapResult = await bootstrapResponse.json();
          
          if (!bootstrapResponse.ok || !bootstrapResult.success) {
            console.error('❌ AuthContext: Bootstrap API error:', bootstrapResult);
            return { error: new Error(bootstrapResult.error || 'Erro ao criar perfil do usuário') };
          }

          console.log('✅ AuthContext: Profile bootstrap successful:', {
            created: bootstrapResult.created,
            profileId: bootstrapResult.profile?.id
          });
        } catch (bootstrapError) {
          console.error('❌ AuthContext: Bootstrap API call failed:', bootstrapError);
          return { error: new Error('Erro ao criar perfil do usuário') };
        }
      } else if (signUpData.user && !signUpData.session) {
        // User needs email confirmation - profile will be created on first login
        console.log('📧 AuthContext: User created, awaiting email confirmation');
      }

      return { error: null };
    } catch (error) {
      console.error('❌ AuthContext: Exception during sign up:', error);
      return { error: new Error('Erro inesperado durante o cadastro') };
    }
  };

  // Helper function for user-friendly error messages
  const getUserFriendlyError = (error: any): string => {
    if (error.message?.includes('User already registered')) {
      return 'Este email já está cadastrado';
    }
    if (error.message?.includes('Invalid email')) {
      return 'Email inválido';
    }
    if (error.message?.includes('Password')) {
      return 'Senha muito fraca';
    }
    return 'Erro durante autenticação';
  };

  // Sign out
  const signOut = async (opts?: { keepBiometric?: boolean }) => {
    // Default: full secure wipe (destructive paths like account deletion and
    // forced 401 sign-out rely on this). The user-facing "Sign out" button
    // passes keepBiometric:true so a biometric-enrolled user can log straight
    // back in with Face ID / Touch ID instead of retyping their password.
    const keepBiometric = opts?.keepBiometric === true;
    try {
      console.log('🔐 AuthContext: Signing out user');

      // Fire-and-forget: capture logout intent before we tear down state.
      // userId is a stable Supabase UUID — not PII per LGPD.
      import('@/utils/clarityEvents')
        .then(({ logClarityEvent }) =>
          logClarityEvent('auth_logout', { userId: user?.id ?? 'unknown' })
        )
        .catch(() => {});

      // CRITICAL: Logout from OneSignal to clear external_id before signing out
      // This prevents notifications from being sent to the wrong user when accounts are switched
      try {
        await oneSignalService.logout();
        console.log('✅ AuthContext: OneSignal user logged out');
      } catch (oneSignalError) {
        console.warn('⚠️ AuthContext: OneSignal logout error:', oneSignalError);
      }

      // Clear biometric credentials unless the caller asked to keep them.
      // Keeping them lets a biometric-enrolled user sign back in with Face ID /
      // Touch ID after a normal sign-out (the stored session is preserved and
      // the Supabase sign-out below uses local scope so the refresh token stays
      // valid). Destructive sign-outs (account deletion, forced 401) leave
      // keepBiometric false and wipe everything.
      let biometricKept = false;
      if (keepBiometric) {
        try {
          biometricKept = await biometricService.isEnabled();
        } catch {
          biometricKept = false;
        }
      }
      if (!biometricKept) {
        try {
          await biometricService.disable();
          console.log('✅ AuthContext: Biometric credentials cleared on sign out');
        } catch (bioError) {
          console.warn('⚠️ AuthContext: Failed to clear biometric credentials:', bioError);
        }
      } else {
        console.log('🔐 AuthContext: Preserving biometric credentials for next sign-in');
      }

      // CRITICAL: Clear validation flag to prevent stray onboarding redirects during sign-out
      try {
        await AsyncStorage.removeItem('validated_user_info');
        console.log('✅ AuthContext: Cleared validated_user_info flag on sign out');
      } catch (clearError) {
        console.warn('⚠️ AuthContext: Failed to clear validated_user_info flag:', clearError);
      }

      // CRITICAL: Clear onboarding-in-progress flag to prevent redirect to onboarding-success on re-login
      try {
        const { OnboardingStateService } = await import('@/services/onboardingStateService');
        await OnboardingStateService.clearOnboardingState();
        console.log('✅ AuthContext: Cleared onboarding-in-progress flag on sign out');
      } catch (clearError) {
        console.warn('⚠️ AuthContext: Failed to clear onboarding-in-progress flag:', clearError);
      }

      // CRITICAL FIX: Clear state IMMEDIATELY - don't rely on onAuthStateChange event
      // The SIGNED_OUT event may not fire reliably in all environments
      console.log('🗑️ AuthContext: Clearing all user state immediately');
      setSession(null);
      setSessionReady(false); // Reset session validation flag
      setUser(null);
      setUserProfile(null);
      setClinicData(null);
      setPlanData(null);
      setError(null);
      
      // Clear API service token + auth-expired callback so it cannot fire
      // during or after sign-out (race-guard against in-flight 401s).
      setAuthExpiredCallback(null);
      clearAccessToken();
      
      // Clear cached data
      try {
        await AsyncStorage.removeItem('userProfile');
        await cacheSessionLocally(null);
        await cacheService.invalidatePattern('user:');
        await clinicCacheService.invalidateAllClinicCaches();
        // CRITICAL: clear profile photo cache too — otherwise the next user
        // who signs up (or logs in) on the same device may briefly see the
        // previous user's avatar on screens that read from the cache.
        try {
          const { default: profilePhotoCacheService } = await import('@/services/profilePhotoCacheService');
          await profilePhotoCacheService.clearAllPhotoCaches();
        } catch (photoCacheError) {
          console.warn('⚠️ AuthContext: Error clearing profile photo cache:', photoCacheError);
        }
        console.log('✅ AuthContext: All caches cleared');
      } catch (cacheError) {
        console.warn('⚠️ AuthContext: Error clearing caches:', cacheError);
      }

      // Call Supabase signOut (this may or may not fire the event). When we are
      // preserving biometric credentials, use local scope so the stored refresh
      // token stays valid for the next biometric sign-in; otherwise revoke
      // globally for a full secure sign-out.
      const { error } = await supabase.auth.signOut(
        biometricKept ? { scope: 'local' } : undefined
      );

      if (error) {
        console.error('❌ AuthContext: Sign out error:', error);
      } else {
        console.log('✅ AuthContext: Sign out successful');
      }

      // CRITICAL FIX: Navigate to welcome page after logout
      // Don't rely on AuthGuard - explicitly navigate
      console.log('🚀 AuthContext: Navigating to welcome page');
      router.replace('/welcome');
      
    } catch (error) {
      console.error('❌ AuthContext: Exception during sign out:', error);
      // Even on error, try to clear state and navigate
      setSession(null);
      setUser(null);
      router.replace('/welcome');
    }
  };

  // ---------------------------------------------------------------------------
  // Force sign-out on unrecoverable 401
  // Fires when apiService exhausts all token-refresh retries and the session
  // cannot be recovered (Supabase refresh token dead / revoked / expired).
  // Rate-limited to once per 30 s inside apiService so concurrent 401 storms
  // only trigger one alert.
  // ---------------------------------------------------------------------------
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);
  const signOutRef = useRef(signOut);
  useEffect(() => { signOutRef.current = signOut; }, [signOut]);

  useEffect(() => {
    if (!user) {
      setAuthExpiredCallback(null);
      return;
    }

    setAuthExpiredCallback(() => {
      Alert.alert(
        tRef.current('auth.sessionExpired.title'),
        tRef.current('auth.sessionExpired.message'),
        [{ text: tRef.current('auth.sessionExpired.button'), onPress: () => signOutRef.current() }]
      );
    });

    return () => { setAuthExpiredCallback(null); };
  }, [user]);

  // Reset password using Brevo email service
  const resetPassword = async (email: string) => {
    try {
      console.log('🔐 AuthContext: Starting password reset for:', email);
      console.log('🔧 AuthContext: __DEV__ flag is:', __DEV__);
      console.log('🔧 AuthContext: Platform is:', Platform.OS);

      // Try multiple API endpoints in order of preference  
      const { env } = await import('@/config/environment');
      const apiEndpoints = [];
      
      // Add configured API URL if available (production/preview builds)
      if (env.apiUrl && env.apiUrl !== env.supabaseUrl) {
        apiEndpoints.push(env.apiUrl);
      }
      
      // Add fallback production endpoint
      if (!apiEndpoints.includes('https://mobile.padrinho.app')) {
        apiEndpoints.push('https://mobile.padrinho.app');
      }
      
      // FIX: Force production API - abandon all localhost usage
      // No localhost endpoints added, even in development
      console.log('🔧 AuthContext: Using production API endpoints only (no localhost)');

      let lastError = null;

      for (const apiUrl of apiEndpoints) {
        try {
          console.log(`🌐 AuthContext: Trying API URL: ${apiUrl}`);
          
          // Create timeout controller for React Native compatibility
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          const response = await fetch(`${apiUrl}/api/auth/reset-password-complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: email.trim().toLowerCase()
            }),
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);

          console.log(`📡 AuthContext: Response from ${apiUrl} - Status: ${response.status}`);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`❌ AuthContext: Server error from ${apiUrl}:`, errorText);
            
            if (response.status === 404) {
              console.log('✅ AuthContext: Server responded with 404 - user not found (server working)');
              return { error: new Error('user not found') };
            }
            
            // Try next endpoint
            lastError = new Error(`Server error: ${response.status}`);
            continue;
          }

          const result = await response.json();
          console.log(`✅ AuthContext: Password reset successful via ${apiUrl}:`, result.success);
          
          return { error: null };

        } catch (networkError) {
          console.error(`❌ AuthContext: Network error with ${apiUrl}:`, {
            message: (networkError as Error).message,
            name: (networkError as Error).name
          });
          lastError = networkError as Error;
          // Continue to next endpoint
        }
      }

      // All endpoints failed - return proper error (no Supabase fallback)
      console.error('❌ AuthContext: All server endpoints failed for password reset');
      console.error('❌ AuthContext: Cannot send password reset - Brevo service unavailable');
      
      if (lastError) {
        console.error('❌ AuthContext: Last error was:', lastError);
        
        // Check if any endpoint was reachable but returned user not found
        if (lastError.message?.includes('user not found')) {
          return { error: new Error('user not found') };
        }
      }
      
      // Return error indicating service is unavailable
      return { error: new Error('Serviço de email temporariamente indisponível. Tente novamente em alguns minutos.') };

    } catch (error) {
      console.error('❌ AuthContext: Exception during password reset:', error);
      return { error };
    }
  };

  // Update profile
  const updateProfile = async (updates: Partial<UserProfile>) => {
    try {
      if (!user) {
        return { error: new Error('No user logged in') };
      }

      console.log('🔄 AuthContext: Updating profile via profileService:', updates);

      const { profileService } = await import('@/services/profileService');
      const result = await profileService.updateProfile(updates);

      if (!result.success) {
        console.error('❌ AuthContext: Error updating profile:', result.error);
        return { error: new Error(result.error || 'Failed to update profile') };
      }

      // Refresh profile data
      await refreshProfile();
      console.log('✅ AuthContext: Profile updated successfully');

      return { error: null };
    } catch (error) {
      console.error('❌ AuthContext: Exception updating profile:', error);
      return { error };
    }
  };

  // Refresh profile
  const refreshProfile = async () => {
    try {
      if (!user) return null;

      console.log('🔄 AuthContext: Refreshing profile');

      // Clear the cache first to ensure fresh data
      const profileCacheKey = `user:profile:${user.id}`;
      await cacheService.invalidate(profileCacheKey);
      console.log('🗑️ AuthContext: Cleared profile cache for fresh fetch');

      // Add timeout protection to prevent indefinite waiting
      const timeoutMs = 10000; // 10 second timeout
      const profilePromise = loadUserProfile(user.id);
      const timeoutPromise = new Promise<null>((_, reject) => {
        setTimeout(() => reject(new Error('Profile refresh timeout')), timeoutMs);
      });

      const profile = await Promise.race([profilePromise, timeoutPromise]);
      
      if (profile) {
        await updateUserProfileState(profile);
        console.log('✅ AuthContext: Profile refreshed');
        return profile; // Return the loaded profile
      }
      return null;
    } catch (error) {
      console.error('❌ AuthContext: Exception refreshing profile:', error);
      return null;
    }
  };

  // Wait for AuthContext.userProfile state to be populated (with polling)
  const waitForProfileReady = async (timeoutMs: number = 5000): Promise<UserProfile | null> => {
    const startTime = Date.now();
    const pollInterval = 100; // Check every 100ms
    
    console.log(`⏳ Waiting for profile state to populate (timeout: ${timeoutMs}ms)...`);
    
    while (Date.now() - startTime < timeoutMs) {
      // Poll the ref (live value) instead of closure variable
      if (userProfileRef.current) {
        const elapsed = Date.now() - startTime;
        console.log(`✅ Profile state populated after ${elapsed}ms`);
        return userProfileRef.current;
      }
      
      // Wait before next check
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
    
    console.error(`❌ Profile state did not populate within ${timeoutMs}ms`);
    return null;
  };

  // Clear all user data (for privacy compliance)
  const clearAllUserData = async () => {
    try {
      console.log('🗑️ AuthContext: Clearing all user data');

      // Clear local storage
      await AsyncStorage.multiRemove([
        'userProfile',
        'sobrietyDate',
        'userNotes',
        'savedMeetings'
      ]);

      // Clear state
      setUser(null);
      setUserProfile(null);
      setSession(null);

      console.log('✅ AuthContext: All user data cleared');
    } catch (error) {
      console.error('❌ AuthContext: Error clearing user data:', error);
    }
  };

  // Request account deletion (immediate deletion from both profiles and auth.users)
  const requestAccountDeletion = async () => {
    try {
      if (!user) {
        console.error('❌ AuthContext: No user found for deletion');
        return { error: new Error('Usuário não encontrado') };
      }

      console.log(`🗑️ AuthContext: Starting immediate account deletion for user: ${user.id.substring(0, 8)}...`);

      // CRITICAL: Clear validation flag BEFORE clearing user data to prevent stray onboarding redirects
      try {
        await AsyncStorage.removeItem('validated_user_info');
        console.log('✅ AuthContext: Cleared validated_user_info flag before account deletion');
      } catch (clearError) {
        console.warn('⚠️ AuthContext: Failed to clear validated_user_info flag:', clearError);
      }

      // Clear all local user data first
      await clearAllUserData();

      // Clear all user caches before deletion
      console.log('🧹 AuthContext: Clearing all user caches before deletion...');
      await invalidateUserCache();

      // Use secure backend API for thorough deletion
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        return { error: new Error('Sessão não encontrada para deletar conta') };
      }

      const response = await fetch('https://mobile.padrinho.app/api/auth/delete-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error('❌ AuthContext: Account deletion failed:', data);
        return { 
          error: new Error(`Erro ao excluir conta: ${data.error || 'Erro desconhecido'}`) 
        };
      }

      // Forget the "last user" snapshot so a deleted account never reappears
      // on the login screen's returning-user card.
      try {
        const { lastUserService } = await import('@/services/lastUserService');
        await lastUserService.clear();
      } catch (lastUserError) {
        console.warn('⚠️ AuthContext: failed to clear last user snapshot on deletion', lastUserError);
      }

      // Sign out the user after successful deletion
      console.log('🔓 AuthContext: Signing out user after successful deletion...');
      await signOut();

      console.log('✅ AuthContext: Account deletion completed successfully');
      return { error: null };

    } catch (error) {
      console.error('❌ AuthContext: Exception during account deletion:', {
        error: error instanceof Error ? error.message : String(error),
        userId: user?.id?.substring(0, 8) + '...',
        timestamp: new Date().toISOString()
      });
      return { error: new Error('Erro inesperado ao excluir conta') };
    }
  };

  // Record user consent
  const recordUserConsent = async (consents: any[]) => {
    try {
      if (!user) {
        return { error: new Error('Usuário não encontrado') };
      }

      console.log('📝 AuthContext: Recording user consents');
      return await ConsentService.recordConsent(user.id, consents);
    } catch (error) {
      console.error('❌ AuthContext: Exception recording consent:', error);
      return { error };
    }
  };

  // Get user consents
  const getUserConsents = async () => {
    try {
      if (!user) {
        return { data: null, error: new Error('Usuário não encontrado') };
      }

      return await ConsentService.getUserConsents(user.id);
    } catch (error) {
      console.error('❌ AuthContext: Exception getting consents:', error);
      return { data: null, error };
    }
  };

  // Check if user has specific consent
  const hasConsent = async (consentType: string) => {
    try {
      if (!user) {
        return { granted: false, error: new Error('Usuário não encontrado') };
      }

      return await ConsentService.hasConsent(user.id, consentType as any);
    } catch (error) {
      console.error('❌ AuthContext: Exception checking consent:', error);
      return { granted: false, error };
    }
  };

  /** @deprecated Consent status is now checked in StartupPromptContext.buildQueue. Not called anywhere; retained for reference only. */
  const checkConsentStatus = async (accessToken: string) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('https://mobile.padrinho.app/api/consent/status', {
        headers: { 'Authorization': `Bearer ${accessToken}` },
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.needs_update) {
          console.log('📋 AuthContext: User needs to re-accept terms');
          setNeedsConsentUpdate(true);
        } else {
          setNeedsConsentUpdate(false);
        }
      }
    } catch (err) {
      console.warn('⚠️ AuthContext: Could not check consent status (non-blocking):', err);
    }
  };

  const dismissConsentUpdate = () => {
    setNeedsConsentUpdate(false);
  };

  const dismissBiometricModal = () => {
    setShowBiometricModal(false);
    pendingBiometricSessionRef.current = null;
  };

  const confirmBiometricEnrolment = async () => {
    try {
      const pendingSession = pendingBiometricSessionRef.current || session;
      if (!pendingSession) {
        setShowBiometricModal(false);
        return;
      }
      await biometricService.storeSession(pendingSession);
      console.log('✅ AuthContext: Biometric enrolment confirmed and session stored');
    } catch (bioError) {
      console.warn('⚠️ AuthContext: Failed to store biometric session:', bioError);
    } finally {
      setShowBiometricModal(false);
      pendingBiometricSessionRef.current = null;
    }
  };

  const acceptConsentUpdate = async (accessToken: string) => {
    try {
      const versionsRes = await fetch('https://mobile.padrinho.app/api/consent/current-version');
      const versionsData = await versionsRes.json();

      const response = await fetch('https://mobile.padrinho.app/api/consent/record', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          consents: [
            { consent_type: 'terms_of_service', version: versionsData.terms_version || '1.0', granted: true },
            { consent_type: 'privacy_policy', version: versionsData.privacy_version || '1.0', granted: true }
          ]
        })
      });

      if (response.ok) {
        console.log('✅ AuthContext: Consent updated successfully');
        setNeedsConsentUpdate(false);
        return { error: null };
      } else {
        const data = await response.json();
        return { error: new Error(data.error || 'Failed to record consent') };
      }
    } catch (error) {
      console.error('❌ AuthContext: Error accepting consent update:', error);
      return { error };
    }
  };

  // Handle magic link authentication
  const handleMagicLink = async (url: string) => {
    try {
      console.log('🔗 AuthContext: Handling magic link');
      
      // STANDBY_MODE check disabled - service is now active for first-access flow
      // Previously checked MagicLinkService.STANDBY_MODE but this is now bypassed

      const result = await MagicLinkService.handleMagicLinkAuth(url);
      
      if (result.success && result.user) {
        // Force refresh the auth state
        await refreshProfile();
      }
      
      return { 
        success: result.success, 
        error: result.error 
      };
    } catch (error) {
      console.error('❌ AuthContext: Exception handling magic link:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  };

  // Check if current user is a patient
  const isPatientUser = async () => {
    try {
      if (!user) return false;
      return await MagicLinkService.isPatientInvite(user.id);
    } catch (error) {
      console.error('❌ AuthContext: Exception checking patient status:', error);
      return false;
    }
  };

  // Check if user needs to set password
  const needsPasswordSetup = async () => {
    try {
      if (!user) return false;
      return await MagicLinkService.needsPasswordSetup(user.id);
    } catch (error) {
      console.error('❌ AuthContext: Exception checking password setup:', error);
      return false;
    }
  };

  // Get clinic data (with caching)
  const getClinicData = async (): Promise<CachedClinicData | null> => {
    try {
      return await clinicCacheService.getUserClinicData();
    } catch (error) {
      console.error('❌ AuthContext: Error getting clinic data:', error);
      return null;
    }
  };

  // Get plan data (with caching)
  const getPlanData = async (): Promise<CachedUserPlan | null> => {
    try {
      if (!user) return null;
      return await clinicCacheService.getUserPlan(user.id);
    } catch (error) {
      console.error('❌ AuthContext: Error getting plan data:', error);
      return null;
    }
  };

  // Refresh all user data (profile, clinic, plan)
  const refreshUserData = async (): Promise<void> => {
    try {
      if (!user) {
        console.warn('⚠️ AuthContext: No user to refresh data for');
        return;
      }

      console.log('🔄 AuthContext: Refreshing all user data');

      // Invalidate caches first to force fresh data
      await invalidateUserCache();

      // Load fresh enhanced user data
      // Pass current session from state (should be populated at this point)
      const enhancedData = await loadEnhancedUserData(user.id, session);
      if (enhancedData) {
        await updateEnhancedUserState(enhancedData);
        console.log('✅ AuthContext: User data refreshed successfully');
      } else {
        console.warn('⚠️ AuthContext: Failed to refresh user data');
      }
    } catch (error) {
      console.error('❌ AuthContext: Error refreshing user data:', error);
    }
  };

  // Invalidate all user-related caches (coordinated across all services)
  const invalidateUserCache = async (): Promise<void> => {
    try {
      if (!user) {
        console.warn('⚠️ AuthContext: No user to invalidate cache for');
        return;
      }

      console.log('🗑️ AuthContext: Invalidating all user-related caches...');

      // Use coordinated cache invalidation from clinicCacheService
      // This ensures all services (AuthContext, ClinicCacheService, BillConfigCacheService) are synchronized
      await clinicCacheService.invalidateUserRelatedCaches(user.id);
      
      // If user has clinic data, also invalidate clinic-specific cache
      if (clinicData?.id) {
        console.log(`🏥 AuthContext: Also invalidating clinic cache for: ${clinicData.name}`);
        await clinicCacheService.invalidateClinicCache(clinicData.id);
      }

      console.log('✅ AuthContext: All user caches invalidated successfully');
    } catch (error) {
      console.error('❌ AuthContext: Error invalidating user cache:', {
        error: error instanceof Error ? error.message : String(error),
        userId: user?.id?.substring(0, 8) + '...',
        timestamp: new Date().toISOString()
      });
    }
  };

  // P09 Phase 1 (PV2-19, PP-15): Wrap the context value in useMemo so that
  // its object identity is stable between renders where no state primitive
  // changed. Callbacks are re-created inline each render but close over the
  // same state values as the deps, so they are never stale within the memo.
  // Any state change invalidates the memo and produces a fresh value with
  // fresh callback closures — no stale-closure risk.
  // Phase 2 (full AuthStateContext / AuthActionsContext split) is tracked in
  // the backlog as a follow-on once all callbacks are stabilised with
  // useCallback and explicit dep arrays.
  const value = useMemo<AuthContextType>(() => ({
    user,
    userProfile,
    clinicData,
    planData,
    session,
    sessionReady,
    loading,
    isInitializing,
    enhancedLoading,
    enhancedReady,
    error,
    retryCount,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile,
    refreshProfile,
    waitForProfileReady,
    clearAllUserData,
    requestAccountDeletion,
    retryInitialization,
    clearError,
    recordUserConsent,
    getUserConsents,
    hasConsent,
    handleMagicLink,
    isPatientUser,
    needsPasswordSetup,
    getClinicData,
    getPlanData,
    refreshUserData,
    invalidateUserCache,
    needsConsentUpdate,
    dismissConsentUpdate,
    acceptConsentUpdate,
    showBiometricModal,
    biometricIsFaceId,
    dismissBiometricModal,
    confirmBiometricEnrolment,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    user, userProfile, clinicData, planData, session, sessionReady,
    loading, isInitializing, enhancedLoading, enhancedReady, error,
    retryCount, needsConsentUpdate, showBiometricModal, biometricIsFaceId,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};